// Amazon Ads, as its own card beside the seller and vendor cards.
//
// With Seller Central connected, advertising is a second authorisation on that
// seller, and the server only accepts the ad account that belongs to it.
// Without Seller Central, Ads can be connected on its own ("ads-only"): every
// ad account the login reaches is kept under its own id, one is active, and the
// card offers a switcher when there is more than one (an agency or consultant
// login). Spend, search terms and campaign checks work ads-only; stock, Buy Box
// and listing checks need Seller Central.
//
// Amazon keeps only sixty days of advertising history, so collecting is not a
// convenience here — anything not pulled before it ages out is gone for good.
// The card reports how much has been stored rather than only whether it is
// connected, because "connected" alone would hide an empty store of history.
import React, { useCallback, useEffect, useState } from 'react';
import styles from './MarketPlaceCards.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE = `${API_URL}/oauth_amazon/ads`;

const headers = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

const call = async (url, token, options = {}) => {
  const response = await fetch(url, { headers: headers(token), ...options });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.error || `Request failed (${response.status})`);
  }
  return data;
};

// What the callback puts in the URL when the seller comes back from Amazon.
// Each one is a different thing to tell them, so none of them is "something
// went wrong".
const ERRORS = {
  seller_mismatch:
    'That Amazon login has no ad account for this seller. Sign in with the account '
    + 'that runs ads for this store, or use "Connect a different ad account" to keep '
    + 'another business\'s ads separately.',
  state_expired: 'The connection took too long. Please try again.',
  state_invalid: 'The connection could not be verified. Please try again.',
  token_exchange_failed: 'Amazon refused the authorisation. Please try again.',
  profiles_failed: 'Connected, but the advertising profiles could not be read.',
  no_refresh_token: 'Amazon did not return a lasting credential. Please try again.',
  missing_code: 'Amazon did not send an authorisation code.',
  no_ads_accounts: 'That Amazon login has no advertising accounts in this marketplace.',
};

const money = (value, currency) =>
  value == null ? '—' : `${currency === 'INR' ? '₹' : ''}${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const AdsLogo = () => (
  <svg width="46" height="46" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 19V11M9.5 19V6M15 19v-5M20.5 19V9"
          stroke="#0e0e0e" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const Facts = ({ status }) => (
  <div className={styles.connectionDetails}>
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>Account:</span>
      <span className={styles.detailValue}>{status.account_name || '—'}</span>
    </div>
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>Profile:</span>
      <span className={styles.detailValue}>{status.profile_id}</span>
    </div>
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>Collected:</span>
      <span className={styles.detailValue}>{status.windows_collected} windows</span>
    </div>
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>Search terms:</span>
      <span className={styles.detailValue}>
        {(status.search_terms || 0).toLocaleString('en-IN')}
      </span>
    </div>
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>Spend:</span>
      <span className={styles.detailValue}>{money(status.spend, status.currency)}</span>
    </div>
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>Ad sales:</span>
      <span className={styles.detailValue}>{money(status.sales, status.currency)}</span>
    </div>
  </div>
);

const AmazonAdsCard = ({ token, sellerConnected }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Loaded whether or not Seller Central is connected: an ads-only connection
  // has a status of its own. Re-read when the seller connection changes.
  const load = useCallback(async () => {
    if (!token) return;
    try {
      setStatus(await call(`${BASE}/status`, token));
    } catch (e) {
      setError(e.message);
    }
  }, [token, sellerConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // The callback redirects back here with the outcome in the query string.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const failed = params.get('ads_error');
    const connected = params.get('ads_connected');
    if (!failed && !connected) return;
    if (failed) setError(ERRORS[failed] || 'The Amazon Ads connection did not complete.');
    if (connected) {
      const found = Number(params.get('accounts') || 0);
      setMessage(params.get('ads_only')
        ? (found > 1
          ? `Amazon Ads connected — ${found} ad accounts found. Amazon does not ask which `
            + 'one, so pick it above, then collect the last 60 days.'
          : 'Amazon Ads connected on its own. Collect the last 60 days to use it.')
        : 'Amazon Ads connected. Collect the last 60 days to use it.');
    }
    ['ads_error', 'ads_connected', 'ads_only', 'accounts'].forEach((key) => params.delete(key));
    const query = params.toString();
    window.history.replaceState({}, '',
      window.location.pathname + (query ? `?${query}` : ''));
    load();
  }, [load]);

  // separate: connect an ad account that is not this seller's (kept under its
  // own id, shown on the Ads pages until disconnected).
  const connect = async (separate = false) => {
    setBusy(true); setError(null);
    try {
      const { auth_url: url } = await call(
        `${BASE}/login${separate ? '?ads_only=1' : ''}`, token);
      window.location.href = url;
    } catch (e) { setError(e.message); setBusy(false); }
  };

  const sync = async () => {
    setSyncing(true); setError(null); setMessage(null);
    try {
      const out = await call(`${BASE}/sync`, token, { method: 'POST', body: JSON.stringify({}) });
      const failed = (out.failed || []).length;
      setMessage(
        `Collected ${out.search_term_rows.toLocaleString('en-IN')} search-term rows across `
        + `${out.windows.length} report windows`
        + (failed ? `, ${failed} window${failed > 1 ? 's' : ''} failed` : '') + '.');
      await load();
    } catch (e) { setError(e.message); } finally { setSyncing(false); }
  };

  const disconnect = async () => {
    setBusy(true); setError(null); setMessage(null);
    try {
      await call(`${BASE}/disconnect`, token, { method: 'POST' });
      setMessage('Amazon Ads disconnected. The reports already collected are kept.');
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // Ads-only logins can reach several businesses' ad accounts; the pages show
  // one at a time.
  const switchAccount = async (accountId) => {
    setBusy(true); setError(null); setMessage(null);
    try {
      await call(`${BASE}/accounts/${encodeURIComponent(accountId)}/activate`, token,
        { method: 'POST' });
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const connected = Boolean(status?.connected);
  const configured = status?.configured !== false;
  const adsOnly = Boolean(status?.ads_only);
  const accounts = status?.accounts || [];

  return (
    <div className={`${styles.card} ${styles.adsCard}`}>
      <div className={styles.connectedBadge}>
        {connected ? (adsOnly ? 'Connected · ads only' : 'Connected') : 'Not connected'}
      </div>

      <div>
        <div className={`${styles.logoContainer} ${styles.adsLogo}`}>
          <AdsLogo />
        </div>
        <h3 className={styles.title}>Amazon Ads</h3>

        {connected && adsOnly && accounts.length > 1 && (
          <label className={styles.adsAccountPicker}>
            <span className={styles.detailLabel}>
              Your login reaches {accounts.length} ad accounts — pick the one to show:
            </span>
            <select
              className={styles.adsAccountSelect}
              value={(accounts.find((a) => a.active) || {}).account_id || ''}
              onChange={(e) => switchAccount(e.target.value)}
              disabled={busy || syncing}
              aria-label="Choose which Amazon Ads account to show"
            >
              {accounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.account_name || a.account_id}
                  {a.account_type === 'vendor' ? ' (vendor)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {connected && (
          <>
            <Facts status={status} />
            <p className={styles.adsNote}>
              Amazon keeps {status.history_days} days of advertising history.
              Anything not collected before it ages out cannot be fetched later.
              {adsOnly && ' Connected without Seller Central: spend, search terms and '
                + 'campaign checks work; stock, Buy Box and listing checks need it.'}
            </p>
          </>
        )}

        {!connected && (
          <p className={styles.adsNote}>
            {!configured
              ? 'Amazon Ads credentials are not configured on the server yet.'
              : sellerConnected
                ? 'Advertising is a second Amazon authorisation. Sign in with the account '
                  + 'that runs ads for this seller to bring spend and paid search terms in.'
                : 'No Seller Central connected — you can still connect Amazon Ads on its own. '
                  + 'Spend, search terms and campaign checks work; stock, Buy Box and '
                  + 'listing checks need Seller Central.'}
          </p>
        )}
      </div>

      <div className={styles.actions}>
        {connected ? (
          <>
            <button
              className={styles.button}
              onClick={sync}
              disabled={syncing || busy}
              aria-label="Collect the last 60 days of Amazon Ads data"
            >
              {syncing ? 'Collecting…' : 'Collect last 60 days'}
            </button>
            <button
              className={styles.secondaryButton}
              onClick={disconnect}
              disabled={syncing || busy}
              aria-label="Disconnect Amazon Ads"
            >
              Disconnect
            </button>
            {/* From this seller's own ads to another business's, without
                disconnecting; on an ads-only connection it refreshes the list. */}
            {(sellerConnected || adsOnly) && (
              <button
                className={styles.secondaryButton}
                onClick={() => connect(true)}
                disabled={syncing || busy || !configured}
                aria-label="Use a different business's Amazon Ads account"
                title="Shows another ad account your Amazon login can reach. It is kept separately; disconnecting it returns to this seller's own ads."
              >
                {adsOnly ? 'Refresh ad accounts' : 'Use a different ad account'}
              </button>
            )}
          </>
        ) : (
          <>
            <button
              className={styles.button}
              onClick={() => connect(false)}
              disabled={busy || !configured}
              aria-label="Connect Amazon Ads account"
            >
              {busy ? 'Opening Amazon…' : 'Connect'}
            </button>
            {sellerConnected && (
              <button
                className={styles.secondaryButton}
                onClick={() => connect(true)}
                disabled={busy || !configured}
                aria-label="Connect a different business's Amazon Ads account"
                title="For an ad account that is not this seller's. It is kept separately and shown on the Ads pages until you disconnect it."
              >
                Connect a different ad account
              </button>
            )}
          </>
        )}
      </div>

      {message && <p className={styles.adsOk}>{message}</p>}
      {error && <p className={styles.adsError}>{error}</p>}
    </div>
  );
};

export default AmazonAdsCard;
